import { Map } from "@shared/types";
import { css, htmlArray } from "../helpers/webComponents";
import { PlayerService } from "../services/PlayerService";
import { Page } from "../enums/Page";

/** CSS styling voor de WereldmapComponent */
const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .title {
        text-align: center;
        margin-top: 10px;
        font-size: 24px;
    }

    .map-container {
        position: relative;
        width: 1000px;
        height: 800px;
        background: url("/assets/img/rooms/wereldmap.png") no-repeat center center;
        background-size: cover;
        background-position: center;
        border: 5px solid #4e4e4e;
        border-radius: 10px;
        margin: auto;
    }

    .room {
        position: absolute;
        width: 80px;
        height: 50px;
        background-color: #780606;
        border: 2px solid #6d6d6d;
        border-radius: 5px;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: bold;
        color: white;
        cursor: pointer;
        transition: all 0.3s ease-in-out;
        box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.5);
        padding: 5px;
        text-transform: capitalize;
    }

    .room:hover {
        transform: scale(1.1);
        background-color: #ff0000;
    }

    .room.current {
        background-color: #bebbbb;
        color: #000000;
        border: 2px solid white;
    }

    .go-back-button {
        background-color: #780606;
        color: white;
        border: none;
        padding: 12px 24px;
        font-size: 18px;
        cursor: pointer;
        border-radius: 5px;
        transition: 0.3s;
        margin: auto;
        display: block;
    }

    .go-back-button:hover {
        background-color: #ff0000;
    }

    .footer {
        border-radius: 10px 10px 0 0;
        background-color: #000000;
        margin-top: 10px;
        display: flex;
        height: auto;
        min-height: 105px;
        max-height: 200px;
    }
`;

export class WereldmapComponent extends HTMLElement {
    private _playerService: PlayerService = new PlayerService();
    private _rooms: string[] = [];
    private _currentRoom: string = "";

    /** Posities van kamers op de wereldkaart */
    private _roomPositions: Record<string, { x: number; y: number }> = {
        "invitationLetter": { x: 850, y: 700 },
        "outsideCastle": { x: 650, y: 700 },
        "castle door": { x: 450, y: 700 },
        "Innerhall": { x: 450, y: 500 },
        "library": { x: 780, y: 400 },
        "castleCourtyard": { x: 450, y: 100 },
        "Office": { x: 790, y: 550 },
        "bedroom": { x: 750, y: 200 },
        "DoorbasementRoom": { x: 300, y: 190 },
        "BasementRoom": { x: 140, y: 190 },
        "diningroom": { x: 170, y: 350 },
        "LivingRoom": { x: 170, y: 550 },
    };

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.fetchWorldMap();
    }

    private goBack(): void {
        const event: CustomEvent = new CustomEvent("game-switch-page", {
            detail: { page: Page.Canvas },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    /** Fetch de wereldmap data */
    private async fetchWorldMap(): Promise<void> {
        try {
            const response: Response = await fetch(`${VITE_API_URL}wereldmap/check-wereldmap`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "X-PlayerSessionId": this._playerService.getPlayerSessionId(),
                },
            });

            if (!response.ok) {
                console.error("Fout bij ophalen wereldmap:", response.status);
                return;
            }

            const data: Map = await response.json() as Map;
            this._rooms = data.rooms;
            this._currentRoom = data.currentRoom || "";

            this.render();
        }
        catch (error) {
            console.error("Fout bij ophalen wereldmap:", error);
        }
    }

    /** Render de wereldkaart */
    private render(): void {
        if (!this.shadowRoot) return;

        const elements: HTMLElement[] = htmlArray`
            <style>${styles}</style>
            ${this.renderTitle()}
            ${this.renderMap()}
            ${this.renderFooter()}
        `;

        this.shadowRoot.innerHTML = "";
        this.shadowRoot.append(...elements);

        // Event listener voor de Go Back-knop toevoegen
        const goBackButton: Element | null = this.shadowRoot.querySelector(".go-back-button");
        goBackButton?.addEventListener("click", () => this.goBack());
    }

    /** Render de titel van de wereldmap */
    private renderTitle(): string {
        return "<div class=\"title\">Wereldmap</div>";
    }

    /** Render de wereldmap met kamers */
    private renderMap(): string {
        const rooms: string = this._rooms.map(room => this.renderRoom(room)).join("");

        return `
            <div class="map-container">
            ${rooms}
            </div>
        `;
    }

    /** Render een individuele kamer op de wereldmap */
    private renderRoom(room: string): string {
        const position: { x: number; y: number } = this._roomPositions[room];

        const isCurrentRoom: "current" | "" = room === this._currentRoom ? "current" : "";

        return `
            <div class="room ${isCurrentRoom}" style="left: ${position.x}px; top: ${position.y}px;">
                ${room.replace("Room", "")} <!-- Naam opschonen -->
            </div>
        `;
    }

    /** Render de footer met de Go Back-knop */
    private renderFooter(): string {
        return `
            <div class="footer">
                <button class="go-back-button">Go Back</button>
            </div>
        `;
    }
}
